import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// --- CUSTOMIZATION AREA ---
// Easily change your theme by updating these classes
const THEME_CLASSES = {
  // This is set to Scheme 1 (Scholarly Blue)
  linkHover: 'hover:text-blue-700',
  
  // Secondary (Login) Button
  secondaryBtnText: 'text-gray-700',
  secondaryBtnHover: 'hover:text-blue-700',

  // Primary (Sign Up) Button
  ctaBg: 'bg-amber-500',
  ctaBgHover: 'hover:bg-amber-600',
  ctaText: 'text-slate-900',
  
  mobileBg: 'bg-white' // Background for the mobile dropdown
};
// --------------------------

const Navbar = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { name: 'Pricing', id: 'pricing' },
    { name: 'Analytics', id: 'analytics' },
    { name: 'About', id: 'about' },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false); // Close mobile menu on click
  };

  // --- NEW FUNCTION ---
  // This function handles the logo click
  const handleLogoClick = () => {
    // Check if we are on the main landing page
    if (window.location.pathname === '/') {
      // If yes, just scroll to the top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // If on another page (like /login), navigate home
      navigate('/');
    }
    setIsMobileMenuOpen(false); // Close mobile menu if open
  };
  // --------------------

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      // This makes it stick to the top and adds the glass effect
      className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md shadow-sm"
    >
      <div className="container mx-auto px-4">
        <div className="py-4 flex justify-between items-center">
          
          {/* Logo */}
          <h1 
            className="text-3xl font-bold text-slate-800 cursor-pointer"
            // --- UPDATED onClick ---
            onClick={handleLogoClick}
          >
            MyReading
          </h1>

          {/* Nav Links & Buttons (Desktop) - Grouped to the right */}
          <div className="hidden md:flex items-center space-x-8">
            
            {/* Nav Links */}
            <div className="flex items-center space-x-6">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.id)}
                  // Uses the customizable hover color
                  className={`text-lg text-gray-700 transition-colors ${THEME_CLASSES.linkHover}`}
                >
                  {item.name}
                </button>
              ))}
            </div>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-300"></div>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-4">
              {/* Login Button (Secondary) */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/login')}
                // Uses the new customizable secondary button colors
                className={`px-5 py-2 rounded-lg font-medium transition-colors ${THEME_CLASSES.secondaryBtnText} ${THEME_CLASSES.secondaryBtnHover}`}
              >
                Login
              </motion.button>

              {/* Sign Up Button (Primary) */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/register')}
                // Uses the customizable CTA colors
                className={`px-5 py-2 rounded-lg font-medium transition-colors ${THEME_CLASSES.ctaBg} ${THEME_CLASSES.ctaText} ${THEME_CLASSES.ctaBgHover}`}
              >
                Sign Up
              </motion.button>
            </div>

          </div>


          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-slate-800"
            >
              {/* Hamburger Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (Dropdown) */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`md:hidden absolute top-full left-0 right-0 shadow-lg ${THEME_CLASSES.mobileBg}`}
        >
          <div className="flex flex-col space-y-4 p-5">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.id)}
                className={`text-lg text-gray-700 text-left ${THEME_CLASSES.linkHover}`}
              >
                {item.name}
              </button>
            ))}
            
            {/* --- ADDED LOGIN BUTTON FOR MOBILE --- */}
            <button
              onClick={() => navigate('/login')}
              className={`text-lg text-gray-700 text-left ${THEME_CLASSES.linkHover}`}
            >
              Login
            </button>

            {/* Sign Up Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/register')}
              className={`w-full px-5 py-3 rounded-lg font-medium transition-colors ${THEME_CLASSES.ctaBg} ${THEME_CLASSES.ctaText} ${THEME_CLASSES.ctaBgHover}`}
            >
              Sign Up
            </motion.button>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;